import React from 'react';
import { BaseFieldProps, Field, WrappedFieldProps } from 'redux-form';
import { Control, FieldValues, UseControllerProps, useController } from 'react-hook-form';
import { omit } from 'lodash';

import FileCropInput, { FileCropInputProps } from './file-crop-input';
import type { Attachment } from '@common/types/objects';

const ReduxFormField = (props: WrappedFieldProps & FileCropInputProps) => {
  return (
    <FileCropInput
      {...props.input}
      {...omit(props, ['meta', 'input'])}
    />
  );
};

export const FileCropInputField = ({
  name, ...props
}: Omit<BaseFieldProps<FileCropInputProps>, 'component'> & FileCropInputProps) => (
  <Field
    {...props}
    name={name}
    component={ReduxFormField}
  />
);

/**
 * React Hook Form wrapper for FileCropInput
 * By default the form validation mode is set to `mode: 'onSubmit'`.
 * This means the validation will be shown after submitting the form.
 * You probably want to set this option to `all` to have immediate feedback when a field does not pass the added rules.
 * useForm({ mode: 'all' });
 *
 * This wrapper saves the file path (URL string) to the form field when upload completes.
 * During upload, it temporarily stores the file object, but once upload completes (Attachment),
 * it saves just the path string to match the expected form value type.
 */
export function FileCropInputHookForm<T extends FieldValues>(
  props: Omit<FileCropInputProps, 'value' | 'onChange'> & UseControllerProps<T> & { control: Control<T> },
) {
  const { field } = useController(props);
  // Convert form value (string URL or file object) to FileCropInput value format
  const getValue = (): Attachment | null => {
    if (!field.value) return null;
    // If it's already a string (URL), convert to Attachment-like object for FileCropInput
    // FileCropInput only needs the path property for display/preview
    if (typeof field.value === 'string') {
      return { path: field.value } as unknown as Attachment;
    }
    // If it's a file object (Attachment or FileInProgress), use as-is
    if (typeof field.value === 'object' && field.value !== null && 'path' in field.value) {
      return field.value as Attachment;
    }
    return null;
  };

  return (
    <FileCropInput
      {...props}
      value={getValue()}
      onChange={(file) => {
        // When file is an Attachment with path (upload complete), save just the path string
        // When file is FileInProgress (uploading), save the object temporarily
        if (file && 'path' in file && file.path && typeof file.path === 'string') {
          // Check if it's an Attachment (has type property) - upload complete
          if ('type' in file && file.type) {
            // Upload complete - save just the URL string
            field.onChange(file.path);
          } else {
            // Still uploading (FileInProgress) - save object temporarily
            field.onChange(file);
          }
        } else {
          field.onChange(null);
        }
      }}
    />
  );
}
